// The Pipeline's stand-alone Lease Calculator: turns the form's text inputs into the
// tested lease and fee engine's inputs and returns everything the popup shows.
// Not tied to a deal — nothing here is saved to the database.

import { analyzeLease, calculateFees, type Abatement, type FeeBasis, type FeeTotals, type LeaseAnalysis, type LeaseInput, type RentStep } from './commission';
import { round2 } from './money';

type S = string; // form fields stay strings while typing

export interface CalcComponent {
  basis: Extract<FeeBasis, 'months_rent' | 'pct_lease_value' | 'flat' | 'per_sf'>;
  rate: S;
}

export interface CalcSide {
  enabled: boolean;
  components: CalcComponent[];
}

export interface CalcState {
  termMonths: S;
  sf: S;
  /** $/SF/yr */
  baseRent: S;
  freeRent: 'none' | 'start' | 'custom';
  /** Months of free rent at the start of the term. */
  freeMonths: S;
  /** Specific free months, e.g. "1, 13, 16" or "1-3, 13". */
  freeCustom: S;
  /** $/SF/yr; blank = not paid on OPEX. */
  opex: S;
  escalation: 'percent' | 'custom';
  escalationPct: S;
  steps: { from: S; to: S; unit: RentStep['unit']; value: S }[];
  thereafterPct: S;
  tenantRep: CalcSide;
  landlordRep: CalcSide;
}

export const SIDE_PRESETS: Record<'tenantRep' | 'landlordRep', { label: string; components: CalcComponent[] }[]> = {
  tenantRep: [
    { label: '1 month + 4%', components: [{ basis: 'months_rent', rate: '1' }, { basis: 'pct_lease_value', rate: '4' }] },
    { label: '3%', components: [{ basis: 'pct_lease_value', rate: '3' }] },
  ],
  landlordRep: [
    { label: '½ month + 2%', components: [{ basis: 'months_rent', rate: '0.5' }, { basis: 'pct_lease_value', rate: '2' }] },
    { label: '3%', components: [{ basis: 'pct_lease_value', rate: '3' }] },
  ],
};

export function emptyCalc(): CalcState {
  return {
    termMonths: '',
    sf: '',
    baseRent: '',
    freeRent: 'none',
    freeMonths: '',
    freeCustom: '',
    opex: '',
    escalation: 'percent',
    escalationPct: '3',
    steps: [],
    thereafterPct: '3',
    tenantRep: { enabled: true, components: structuredClone(SIDE_PRESETS.tenantRep[0]!.components) },
    landlordRep: { enabled: true, components: structuredClone(SIDE_PRESETS.landlordRep[0]!.components) },
  };
}

const num = (s: S): number | null => {
  const t = s.replace(/[$,\s%]/g, '');
  return t === '' || isNaN(Number(t)) ? null : Number(t);
};

/** "1, 13, 16" or "1-3, 13" -> [1, 2, 3, 13]. Throws on anything else. */
export function parseMonths(text: S): number[] {
  const out = new Set<number>();
  for (const part of text.split(/[,;\s]+/).filter(Boolean)) {
    const m = /^(\d+)(?:-(\d+))?$/.exec(part);
    if (!m) throw new Error(`"${part}" isn't a month number. Use whole numbers like 1, 13, 16 or a range like 1-3.`);
    const a = Number(m[1]);
    const b = m[2] ? Number(m[2]) : a;
    if (a < 1 || b < a) throw new Error(`"${part}" isn't a valid month range.`);
    for (let i = a; i <= b; i++) out.add(i);
  }
  return [...out].sort((x, y) => x - y);
}

/** Consecutive free months become one abatement period. */
function abatementsFor(months: number[]): Abatement[] {
  const out: Abatement[] = [];
  for (const m of months) {
    const last = out[out.length - 1];
    if (last && last.startMonth + last.months === m) last.months++;
    else out.push({ startMonth: m, months: 1, pct: 100 });
  }
  return out;
}

export function toLeaseInput(c: CalcState): LeaseInput {
  const term = num(c.termMonths);
  const sf = num(c.sf);
  if (!term) throw new Error('Enter the lease term in months.');
  if (!sf) throw new Error('Enter the suite square footage.');

  let escalation: LeaseInput['escalation'];
  let baseRent = num(c.baseRent);
  if (c.escalation === 'percent') {
    escalation = { kind: 'percent', annualPct: num(c.escalationPct) ?? 0 };
  } else {
    const steps: RentStep[] = c.steps.map((st, i) => {
      const from = num(st.from);
      const to = num(st.to);
      const value = num(st.value);
      if (from === null || to === null || value === null) throw new Error(`Rent step ${i + 1} needs its months and an amount.`);
      return { fromMonth: from, toMonth: to, unit: st.unit, value };
    });
    escalation = { kind: 'steps', steps, thereafterPct: num(c.thereafterPct) ?? 0 };
    // When the first step sets month 1's rent in dollars, the base rent box can stay empty.
    const first = steps[0];
    if (baseRent === null && first?.fromMonth === 1 && first.unit !== 'pct_increase') {
      baseRent = first.unit === 'psf_year' ? first.value : (first.value * 12) / sf;
    }
  }
  if (!baseRent) throw new Error('Enter the base rent ($/SF/yr).');

  let free: number[] = [];
  if (c.freeRent === 'start') {
    const n = num(c.freeMonths);
    if (!n || !Number.isInteger(n)) throw new Error('Enter how many months of free rent.');
    free = Array.from({ length: n }, (_, i) => i + 1);
  } else if (c.freeRent === 'custom') {
    free = parseMonths(c.freeCustom);
    if (free.length === 0) throw new Error('Enter the free-rent months, e.g. 1, 13, 16.');
  }
  const beyond = free.find((m) => m > term);
  if (beyond) throw new Error(`Free month ${beyond} is past the end of a ${term}-month term.`);

  const opex = num(c.opex);
  return {
    rentableSf: sf,
    termMonths: term,
    baseRent: { mode: 'psf_year', value: baseRent },
    escalation,
    abatements: abatementsFor(free),
    opex: { enabled: opex !== null && opex > 0, psfYear: opex },
  };
}

function sideFees(side: CalcSide, lease: LeaseAnalysis, name: string): FeeTotals | null {
  if (!side.enabled || side.components.length === 0) return null;
  return calculateFees(
    side.components.map((comp, i) => ({ key: `${name}_${i}`, basis: comp.basis, rate: num(comp.rate), payer: 'landlord' })),
    { lease },
  );
}

export interface ScheduleRow {
  from: number;
  to: number;
  monthlyRent: number;
  psfYear: number;
  free: boolean;
}

/** The month-by-month schedule collapsed into runs of identical rent. */
export function groupSchedule(lease: LeaseAnalysis): ScheduleRow[] {
  const rows: ScheduleRow[] = [];
  for (const m of lease.schedule) {
    const rent = round2(m.baseRent);
    const free = m.abatedPct >= 100;
    const last = rows[rows.length - 1];
    if (last && last.monthlyRent === rent && last.free === free && last.to === m.month - 1) last.to = m.month;
    else rows.push({ from: m.month, to: m.month, monthlyRent: rent, psfYear: round2((rent * 12) / lease.rentableSf), free });
  }
  return rows;
}

export interface CalcResult {
  lease: LeaseAnalysis | null;
  tenantRep: FeeTotals | null;
  landlordRep: FeeTotals | null;
  total: number;
  error: string | null;
}

export function runCalculator(c: CalcState): CalcResult {
  try {
    const lease = analyzeLease(toLeaseInput(c));
    const tenantRep = sideFees(c.tenantRep, lease, 'tr');
    const landlordRep = sideFees(c.landlordRep, lease, 'lr');
    return { lease, tenantRep, landlordRep, total: round2((tenantRep?.gross ?? 0) + (landlordRep?.gross ?? 0)), error: null };
  } catch (err) {
    return { lease: null, tenantRep: null, landlordRep: null, total: 0, error: err instanceof Error ? err.message : String(err) };
  }
}
